import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";
import { Church, Mail, Copy, UserCircle2, Send } from "lucide-react";
import { Link } from "react-router-dom";

export default function UndanganJemaat() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/invitations/jemaat")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);

  const copyEmail = async (email) => {
    try {
      await navigator.clipboard.writeText(email);
      toast.success(`Email ${email} disalin.`);
    } catch { toast.error("Gagal menyalin."); }
  };

  const copyInviteMessage = async (row) => {
    const gembala = row.accounts.find(a => a.role === "GEMBALA_JEMAAT");
    const operator = row.accounts.find(a => a.role === "SEKRETARIS_JEMAAT");
    const msg = `Selamat pagi, Pdt. Gembala ${row.church.short_name}.

Kami dari Sekretariat GPDP Klasis Mimika mengundang Bapak/Ibu untuk melengkapi data ${row.church.name} pada aplikasi SIMPELKAMI GPDP.

Akses aplikasi:
${window.location.origin}/login

Akun Gembala:  ${gembala?.email || "-"}
Akun Operator: ${operator?.email || "-"}
Password awal: Simpel@2026 (harap segera diubah setelah login pertama)

Data yang perlu dilengkapi: profil jemaat, jumlah jiwa, gembala, sekretaris, bendahara, dan foto gereja.

Salam Pelayanan,
BPK GPDP Klasis Mimika`;
    try {
      await navigator.clipboard.writeText(msg);
      toast.success("Pesan undangan disalin — siap tempel ke WhatsApp / Email.");
    } catch { toast.error("Gagal menyalin pesan."); }
  };

  return (
    <AppShell>
      <div className="mb-6">
        <div className="text-xs tracking-[0.18em] uppercase text-muted-foreground">Sistem</div>
        <h1 className="font-heading font-bold text-3xl mt-1">Undangan Jemaat</h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
          Setiap jemaat memiliki dua akun: <strong>Gembala</strong> (untuk approval) dan <strong>Operator/Sekretaris</strong> (untuk input data harian).
          Bagikan email &amp; password awal kepada gembala jemaat masing-masing. Password wajib diubah setelah login pertama.
        </p>
      </div>

      {loading ? <div className="text-sm text-muted-foreground">Memuat…</div> : !data ? null : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Card><CardContent className="p-5">
              <div className="text-[0.65rem] tracking-[0.18em] uppercase text-muted-foreground">Total Jemaat</div>
              <div className="font-heading font-bold text-3xl mt-1">{data.total}</div>
            </CardContent></Card>
            <Card><CardContent className="p-5">
              <div className="text-[0.65rem] tracking-[0.18em] uppercase text-muted-foreground">Data Terverifikasi</div>
              <div className="font-heading font-bold text-3xl mt-1 text-primary">{data.completed}<span className="text-xl text-muted-foreground">/{data.total}</span></div>
            </CardContent></Card>
            <Card><CardContent className="p-5">
              <div className="text-[0.65rem] tracking-[0.18em] uppercase text-muted-foreground">Password Awal</div>
              <div className="font-heading font-bold text-lg mt-2 font-mono">Simpel@2026</div>
              <div className="text-xs text-muted-foreground mt-1">Harap diubah setelah login pertama.</div>
            </CardContent></Card>
          </div>

          <div className="space-y-3">
            {data.rows.map((r) => {
              const gembala = r.accounts.find(a => a.role === "GEMBALA_JEMAAT");
              const operator = r.accounts.find(a => a.role === "SEKRETARIS_JEMAAT");
              const hasLogin = r.accounts.some(a => a.last_login);
              return (
                <Card key={r.church.id} data-testid={`invite-row-${r.church.code}`}>
                  <CardContent className="p-5 grid grid-cols-1 lg:grid-cols-12 gap-4 items-center">
                    <div className="lg:col-span-4">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-md bg-primary/10 text-primary flex items-center justify-center">
                          <Church className="h-5 w-5" />
                        </div>
                        <div className="min-w-0">
                          <div className="text-[0.65rem] tracking-[0.18em] uppercase text-muted-foreground">{r.church.code}</div>
                          <Link to={`/jemaat/${r.church.id}`} className="font-heading font-bold text-sm hover:text-primary transition-colors block truncate">{r.church.name}</Link>
                        </div>
                      </div>
                    </div>

                    <div className="lg:col-span-2">
                      <div className="text-[0.65rem] uppercase text-muted-foreground mb-1">Kelengkapan Profil</div>
                      <div className="flex items-center gap-2">
                        <Progress value={r.completion_pct} className="h-1.5 flex-1" />
                        <span className="text-xs font-mono w-8 text-right">{r.completion_pct}%</span>
                      </div>
                      <div className="mt-1"><StatusBadge status={r.verification_status} /></div>
                    </div>

                    <div className="lg:col-span-4 space-y-1.5">
                      {[gembala, operator].filter(Boolean).map((a) => (
                        <div key={a.email} className="flex items-center gap-2 text-xs">
                          <UserCircle2 className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                          <span className="text-[0.65rem] tracking-wider uppercase text-muted-foreground w-16">{a.role === "GEMBALA_JEMAAT" ? "Gembala" : "Operator"}</span>
                          <span className="font-mono truncate flex-1">{a.email}</span>
                          <button onClick={()=>copyEmail(a.email)} className="text-primary hover:text-secondary transition-colors" data-testid={`btn-copy-email-${a.email}`} title="Salin">
                            <Copy className="h-3 w-3" />
                          </button>
                          {a.last_login && <StatusBadge status="success" label="Aktif" />}
                        </div>
                      ))}
                    </div>

                    <div className="lg:col-span-2 flex flex-col gap-1.5">
                      <Button size="sm" variant="outline" className="rounded-full h-8 text-xs" onClick={()=>copyInviteMessage(r)} data-testid={`btn-invite-msg-${r.church.code}`}>
                        <Send className="h-3 w-3 mr-1.5" /> Salin Pesan
                      </Button>
                      <div className="text-[0.65rem] text-muted-foreground text-center">
                        {hasLogin ? "Sudah login" : "Belum pernah login"}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="mt-6 text-xs text-muted-foreground bg-muted/40 border border-border rounded-md p-4 flex items-start gap-3">
            <Mail className="h-4 w-4 mt-0.5 text-primary shrink-0" />
            <div>
              <strong>Cara mengundang:</strong> Tekan "Salin Pesan" pada setiap baris untuk mendapatkan template undangan siap tempel ke WhatsApp atau email gembala jemaat.
              Pesan sudah berisi link login, email akun gembala &amp; operator, serta password awal.
              Setelah gembala login untuk pertama kali, kolom "Aktif" akan berubah warna.
            </div>
          </div>
        </>
      )}
    </AppShell>
  );
}
